import React, { useState, useEffect } from 'react';
import { Filter, MapPin, Truck, ChevronDown, ChevronUp, Calendar, TrendingUp, X } from 'lucide-react';
import { 
  PieChart as RechartsPie, 
  Pie, 
  Cell, 
  Tooltip, 
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend
} from 'recharts';
import { useUser } from '../contexts/UserContext';
import { getRateInsights, getCityDetails } from '../services/backendApiService';
import { useActivityLogger } from '../hooks/useActivityLogger';

const RateInsights = () => {
  const { userRole } = useUser();
  const { logRateInsights } = useActivityLogger();
  
  // State
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(() => {
    // Load saved equipment groups from localStorage
    let savedEquipmentGroups = ['Flatbed', 'Step Deck', 'Dry Van', 'Reefer', 'Hot Shot']; // default
    try {
      const stored = localStorage.getItem('rate_insights_equipment_groups');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          savedEquipmentGroups = parsed;
        }
      }
    } catch (error) {
      console.warn('Error loading equipment groups from localStorage:', error);
    }

    return {
      dateRange: { preset: 'last-3-weeks', start: '', end: '' },
      equipmentGroups: savedEquipmentGroups,
      loadTypes: ['Full'],
      minPayment: 0,
      minRatePerMile: 0.0,
      maxLength: 75,
      maxWeight: 100000,
      includeIntraState: false
    };
  });
  const [displayOptions, setDisplayOptions] = useState({ topStates: 25, topCities: 25 });
  const [statesSortConfig, setStatesSortConfig] = useState({ key: 'loads_out', direction: 'desc' });
  const [citiesSortConfig, setCitiesSortConfig] = useState({ key: 'count', direction: 'desc' });
  const [visibility, setVisibility] = useState({ filters: true, chart: true, daily: true, states: true, cities: true, equipment: true });
  const [showWeekends, setShowWeekends] = useState(false);
  const [showRollingAverage, setShowRollingAverage] = useState(false);
  const [cityDetailsModal, setCityDetailsModal] = useState({
    isOpen: false,
    city: '',
    state: '',
    data: null,
    loading: false,
    error: null,
    showRawData: false
  });

  // Constants
  const equipmentMap = {
    'Flatbed': ['F', 'FA', 'CONG'],
    'Step Deck': ['FSD', 'SD', 'SDL'],
    'Dry Van': ['V', 'VR', 'SV'],
    'Reefer': ['R', 'VR'],
    'Hot Shot': ['HS']
  };
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  // Helpers
  const toggle = (section) => setVisibility(prev => ({ ...prev, [section]: !prev[section] }));
  
  const calculateDateRange = (preset) => {
    const today = new Date();
    const end = new Date(today);
    const start = new Date(today);
    
    if (preset === 'next-7-days') {
      // For next 7 days: start = today, end = today + 7
      end.setDate(today.getDate() + 7);
    } else if (preset === 'all') {
      // For all: start = today - 28, end = today + 7
      start.setDate(today.getDate() - 28);
      end.setDate(today.getDate() + 7);
    } else {
      // For past periods: start = today + negative days, end = today
      const days = { 'last-4-weeks': -28, 'last-3-weeks': -21, 'last-2-weeks': -14, 'last-week': -7 };
      start.setDate(today.getDate() + (days[preset] || -7));
    }
    
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  };

  // Initialize dates
  useEffect(() => {
    const dateRange = calculateDateRange('last-3-weeks');
    setFilters(prev => ({ ...prev, dateRange: { preset: 'last-3-weeks', ...dateRange } }));
  }, []);

  // Filter daily data to exclude weekends if needed and calculate 5-day rolling average
  const getFilteredDailyData = React.useMemo(() => {
    const dailyData = data?.daily_data || [];
    
    // First filter weekends if needed
    let filteredData = dailyData;
    if (!showWeekends) {
      filteredData = dailyData.filter(d => {
        const [year, month, day] = d.date.split('-');
        const date = new Date(year, month - 1, day);
        const dayOfWeek = date.getDay();
        return dayOfWeek !== 0 && dayOfWeek !== 6; // Exclude Sunday (0) and Saturday (6)
      });
    }
    
    // Sort by date to ensure proper order for rolling average (use string comparison to avoid timezone issues)
    const sortedData = [...filteredData].sort((a, b) => a.date.localeCompare(b.date));
    
    // Calculate 5-day rolling average for rate per mile
    return sortedData.map((day, index) => {
      let rollingAvg = day.avg_rate_per_mile;
      
      if (index >= 4) { // Need at least 5 days for rolling average
        const last5Days = sortedData.slice(index - 4, index + 1);
        const validRates = last5Days.filter(d => d.avg_rate_per_mile && d.avg_rate_per_mile > 0);
        
        if (validRates.length >= 3) { // Need at least 3 valid data points
          rollingAvg = validRates.reduce((sum, d) => sum + d.avg_rate_per_mile, 0) / validRates.length;
        }
      }
      
      return {
        ...day,
        rolling_5day_rpm: rollingAvg
      };
    });
  }, [data?.daily_data, showWeekends]);

  // Trend Cards Calculation
  const trendCards = React.useMemo(() => {
    const dailyData = getFilteredDailyData;
    if (!dailyData.length) return null;
    
    // Sort data chronologically (use string comparison to avoid timezone issues)
    const sortedData = [...dailyData].sort((a, b) => a.date.localeCompare(b.date));
    
    // Get today's date in Mountain Time and find it in the data
    const today = new Date();
    const mountainTime = new Date(today.toLocaleString("en-US", {timeZone: "America/Denver"}));
    const todayString = mountainTime.toISOString().split('T')[0]; // YYYY-MM-DD format
    
    // Find today's data (or the most recent available data before today)
    let currentDayIndex = sortedData.findIndex(day => day.date === todayString);
    if (currentDayIndex === -1) {
      // If today's data doesn't exist, find the most recent day before today
      for (let i = sortedData.length - 1; i >= 0; i--) {
        if (sortedData[i].date <= todayString) {
          currentDayIndex = i;
          break;
        }
      }
    }
    
    if (currentDayIndex === -1) return null; // No data found for today or before
    
    const currentDay = sortedData[currentDayIndex];
    if (!currentDay?.rolling_5day_rpm) return null;
    
    // Helper function to create trend card
    const createTrendCard = (title, daysAgo, bgColor, textColor) => {
      let targetIndex = currentDayIndex - daysAgo;
      
      if (targetIndex < 0) return null;
      
      // If the exact target doesn't have a rolling average, find the closest later day that does
      while (targetIndex < sortedData.length && !sortedData[targetIndex]?.rolling_5day_rpm) {
        targetIndex++;
      }
      
      if (targetIndex >= sortedData.length || !sortedData[targetIndex]?.rolling_5day_rpm) return null;
      
      const targetDay = sortedData[targetIndex];
      const currentRpm = currentDay.rolling_5day_rpm;
      const targetRpm = targetDay.rolling_5day_rpm;
      const percentChange = ((currentRpm - targetRpm) / targetRpm * 100);
      const isPositive = percentChange > 0;
      
      return (
        <div key={title} className={`p-4 rounded-lg border ${bgColor}`}>
          <div className={`text-sm font-medium ${textColor} mb-1`}>
            {title}
          </div>
          <div className={`text-lg font-bold ${textColor} flex items-center`}>
            {isPositive ? '↗' : '↘'} {Math.abs(percentChange).toFixed(1)}%
            <span className={`ml-2 text-xs ${textColor.replace('700', '500').replace('300', '400')}`}>
              ${targetRpm.toFixed(2)} → ${currentRpm.toFixed(2)}
            </span>
          </div>
        </div>
      );
    };
    
    const weeklyCard = createTrendCard(
      "Weekly Trend", 
      7, 
      "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800",
      "text-blue-700 dark:text-blue-300"
    );
    
    const twoWeekCard = createTrendCard(
      "2-Week Trend", 
      14, 
      "bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800",
      "text-purple-700 dark:text-purple-300"
    );
    
    // Only show if we have at least one card
    if (!weeklyCard && !twoWeekCard) return null;
    
    return (
      <div className="mb-4 grid gap-4" style={{ gridTemplateColumns: `repeat(${[weeklyCard, twoWeekCard].filter(Boolean).length}, 1fr)` }}>
        {weeklyCard}
        {twoWeekCard}
      </div>
    );
  }, [getFilteredDailyData]);

  // Components
  const Section = ({ section, icon: Icon, title, iconColor, children }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700" onClick={() => toggle(section)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon className={`w-5 h-5 ${iconColor}`} />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
          </div>
          {visibility[section] ? <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />}
        </div>
      </div>
      {visibility[section] && <div className="p-6">{children}</div>}
    </div>
  );

  const Table = ({ headers, data, renderRow }) => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-700">
          <tr>{headers.map((h, i) => <th key={i} className={`px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase ${h.align || 'text-left'}`}>{h.label}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
          {data.map((item, i) => <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700">{renderRow(item)}</tr>)}
        </tbody>
      </table>
    </div>
  );

  const RangeInput = ({ label, value, min, max, step = 1, onChange, format = (v) => v }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{label}: {format(value)}</label>
      <input type="range" min={min} max={max} step={step} value={value} onChange={onChange} className="w-full" />
    </div>
  );

  // API call
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const equipmentTypes = filters.equipmentGroups.flatMap(group => equipmentMap[group] || [group]);
      const apiFilters = {
        pickupDateBegin: filters.dateRange.start,
        pickupDateEnd: filters.dateRange.end,
        equipmentTypes: equipmentTypes.length ? equipmentTypes : undefined,
        loadTypes: filters.loadTypes.length ? filters.loadTypes : undefined,
        minPayment: filters.minPayment || undefined,
        minRatePerMile: filters.minRatePerMile || undefined,
        maxLength: filters.maxLength || undefined,
        maxWeight: filters.maxWeight || undefined,
        includeIntraState: filters.includeIntraState
      };
      
      const result = await getRateInsights(apiFilters);
      setData(result);

      // Log rate insights activity using centralized logger
      try {
        await logRateInsights({
          pickup_date_begin: filters.dateRange.start,
          pickup_date_end: filters.dateRange.end,
          equipment_types: filters.equipmentGroups,
          load_types: filters.loadTypes,
          min_payment: filters.minPayment,
          min_rate_per_mile: filters.minRatePerMile,
          max_length: filters.maxLength,
          max_weight: filters.maxWeight,
          include_intra_state: filters.includeIntraState
        });
      } catch (logError) {
        console.warn('Failed to log rate insights activity:', logError);
      }
    } catch (err) {
      if (err.response?.status === 429 && err.rateLimitInfo) {
        const { retryAfter, remainingRequests, resetTime } = err.rateLimitInfo;
        setError(`Rate limit exceeded. Wait ${retryAfter}s. Remaining: ${remainingRequests}. Reset: ${resetTime?.toLocaleTimeString() || 'unknown'}`);
      } else {
        setError('Failed to load rate insights data');
      }
    } finally {
      setLoading(false);
    }
  };

  // Sorting functions
  const handleStatesSort = (key) => {
    let direction = 'asc';
    if (statesSortConfig.key === key && statesSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setStatesSortConfig({ key, direction });
  };

  const getSortedStatesData = () => {
    if (!data?.states_data) return [];

    const sortedData = [...data.states_data].sort((a, b) => {
      // Calculate total loads for sorting
      if (statesSortConfig.key === 'total') {
        const aTotal = (a.loads_in || 0) + (a.loads_out || 0);
        const bTotal = (b.loads_in || 0) + (b.loads_out || 0);
        return statesSortConfig.direction === 'asc' ? aTotal - bTotal : bTotal - aTotal;
      }

      // Handle string sorting for state names
      if (statesSortConfig.key === 'state') {
        const aVal = a.state || '';
        const bVal = b.state || '';
        return statesSortConfig.direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      // Handle numeric sorting for all other fields
      const aVal = a[statesSortConfig.key] || 0;
      const bVal = b[statesSortConfig.key] || 0;

      if (statesSortConfig.direction === 'asc') {
        return aVal - bVal;
      } else {
        return bVal - aVal;
      }
    });

    return sortedData.slice(0, displayOptions.topStates);
  };

  const handleCitiesSort = (key) => {
    let direction = 'asc';
    if (citiesSortConfig.key === key && citiesSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setCitiesSortConfig({ key, direction });
  };

  const getSortedCitiesData = () => {
    if (!data?.cities_data) return [];

    const sortedData = [...data.cities_data].sort((a, b) => {
      // Handle string sorting for city_state field
      if (citiesSortConfig.key === 'city_state') {
        const aVal = a.city_state || '';
        const bVal = b.city_state || '';
        return citiesSortConfig.direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      // Handle numeric sorting for all other fields
      const aVal = a[citiesSortConfig.key] || 0;
      const bVal = b[citiesSortConfig.key] || 0;

      if (citiesSortConfig.direction === 'asc') {
        return aVal - bVal;
      } else {
        return bVal - aVal;
      }
    });

    return sortedData.slice(0, displayOptions.topCities);
  };

  // Save equipment groups to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('rate_insights_equipment_groups', JSON.stringify(filters.equipmentGroups));
      console.log('[RateInsights] Saved equipment groups to localStorage:', filters.equipmentGroups);
    } catch (error) {
      console.warn('Error saving equipment groups to localStorage:', error);
    }
  }, [filters.equipmentGroups]);

  // Handle city click to show details
  const handleCityClick = async (cityStateString) => {
    try {
      // Parse "City, State" format
      const parts = cityStateString.split(', ');
      if (parts.length !== 2) {
        console.error('Invalid city state format:', cityStateString);
        return;
      }

      const [city, state] = parts;

      // Set modal state to loading
      setCityDetailsModal({
        isOpen: true,
        city,
        state,
        data: null,
        loading: true,
        error: null
      });

      // Prepare API parameters using current filters
      const equipmentTypes = filters.equipmentGroups.flatMap(group => equipmentMap[group] || [group]);

      // Ensure we have valid dates - if not set, use a reasonable default range
      let dateStart = filters.dateRange.start;
      let dateEnd = filters.dateRange.end;

      if (!dateStart || !dateEnd) {
        // Calculate default date range if not set
        const dateRange = calculateDateRange(filters.dateRange.preset || 'last-3-weeks');
        dateStart = dateRange.start;
        dateEnd = dateRange.end;
      }

      const cityParams = {
        city,
        state,
        pickupDateBegin: dateStart,
        pickupDateEnd: dateEnd,
        equipmentTypes: equipmentTypes.length ? equipmentTypes : undefined,
        loadTypes: filters.loadTypes.length ? filters.loadTypes : undefined,
        // Use more lenient filters for city details to show more loads
        minPayment: 0, // Don't restrict by minimum payment
        minRatePerMile: 0, // Don't restrict by minimum rate per mile
        includeIntraState: filters.includeIntraState,
        // Request individual load details
        includeLoads: true,
        limit: 1000 // Limit the number of individual loads returned
      };

      // Fetch city details
      const cityData = await getCityDetails(cityParams);

      // Update modal with data
      setCityDetailsModal(prev => ({
        ...prev,
        data: cityData,
        loading: false,
        error: null
      }));

    } catch (error) {
      console.error('Error fetching city details:', error);
      setCityDetailsModal(prev => ({
        ...prev,
        data: null,
        loading: false,
        error: 'Failed to load city details'
      }));
    }
  };

  const closeCityDetailsModal = () => {
    setCityDetailsModal({
      isOpen: false,
      city: '',
      state: '',
      data: null,
      loading: false,
      error: null,
      showRawData: false
    });
  };

  const toggleRawDataView = () => {
    setCityDetailsModal(prev => ({
      ...prev,
      showRawData: !prev.showRawData
    }));
  };

  // Note: Removed full-screen loading states to allow filters to always show

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Rate Insights</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">Freight rate analysis and market insights</p>
      </div>

      {/* Basic Plan Warning */}
      {userRole === 'Basic' && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <div className="w-5 h-5 bg-yellow-100 dark:bg-yellow-800 rounded-full flex items-center justify-center">
              <span className="text-yellow-600 dark:text-yellow-400 text-xs">!</span>
            </div>
            <div>
              <p className="text-yellow-700 dark:text-yellow-300 text-sm font-medium">Basic Plan Limitation</p>
              <p className="text-yellow-600 dark:text-yellow-400 text-sm">Basic users have daily API limits. Upgrade for unlimited access.</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <Section section="filters" icon={Filter} title="Filters" iconColor="text-gray-600 dark:text-gray-400">
        {/* First Row: Date Range, Load Scope, Load Types */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {/* Date Range */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Date Range</label>
            <select
              value={filters.dateRange.preset}
              onChange={(e) => {
                const preset = e.target.value;
                const dateRange = calculateDateRange(preset);
                setFilters(prev => ({ ...prev, dateRange: { preset, ...dateRange } }));
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="last-4-weeks">Last 4 Weeks</option>
              <option value="last-3-weeks">Last 3 Weeks</option>
              <option value="last-2-weeks">Last 2 Weeks</option>
              <option value="last-week">Last Week</option>
              <option value="next-7-days">Next 7 Days</option>
              <option value="all">All (4 weeks + 7 days)</option>
            </select>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {filters.dateRange.start && filters.dateRange.end && `${filters.dateRange.start} to ${filters.dateRange.end}`}
            </div>
          </div>

          {/* Intra-State Filter */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Load Scope</label>
            <div className="space-y-1">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters.includeIntraState}
                  onChange={(e) => {
                    setFilters(prev => ({
                      ...prev,
                      includeIntraState: e.target.checked
                    }));
                  }}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Include Intra-State</span>
              </label>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Include loads within same state (TX→TX)
              </div>
            </div>
          </div>

          {/* Load Types */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Load Types</label>
            <div className="space-y-1">
              {['Full', 'Partial'].map(option => (
                <label key={option} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.loadTypes.includes(option)}
                    onChange={(e) => {
                      setFilters(prev => ({
                        ...prev,
                        loadTypes: e.target.checked
                          ? [...prev.loadTypes, option]
                          : prev.loadTypes.filter(t => t !== option)
                      }));
                    }}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{option}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Second Row: Equipment Groups and Range Sliders */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Equipment Groups */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Equipment Groups</label>
            <div className="space-y-1">
              {['Flatbed', 'Step Deck', 'Dry Van', 'Reefer', 'Hot Shot'].map(option => (
                <label key={option} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.equipmentGroups.includes(option)}
                    onChange={(e) => {
                      setFilters(prev => ({
                        ...prev,
                        equipmentGroups: e.target.checked
                          ? [...prev.equipmentGroups, option]
                          : prev.equipmentGroups.filter(g => g !== option)
                      }));
                    }}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{option}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Min Rate/Mile */}
          <RangeInput
            label="Min Rate/Mile"
            value={filters.minRatePerMile}
            min="0" max="5" step="0.1"
            onChange={(e) => setFilters(prev => ({ ...prev, minRatePerMile: parseFloat(e.target.value) }))}
            format={(v) => `$${v.toFixed(2)}`}
          />

          {/* Min Payment */}
          <RangeInput
            label="Min Payment"
            value={filters.minPayment}
            min="0" max="1000" step="50"
            onChange={(e) => setFilters(prev => ({ ...prev, minPayment: parseInt(e.target.value) }))}
            format={(v) => `$${v}`}
          />

          {/* Max Length */}
          <RangeInput
            label="Max Length"
            value={filters.maxLength}
            min="0" max="75"
            onChange={(e) => setFilters(prev => ({ ...prev, maxLength: parseInt(e.target.value) }))}
            format={(v) => `${v}ft`}
          />

          {/* Max Weight */}
          <RangeInput
            label="Max Weight"
            value={filters.maxWeight}
            min="0" max="100000" step="1000"
            onChange={(e) => setFilters(prev => ({ ...prev, maxWeight: parseInt(e.target.value) }))}
            format={(v) => `${(v / 1000).toFixed(0)}k lbs`}
          />
        </div>

        {/* Chart Options */}
        <div className="mt-4 flex items-center space-x-6">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="showWeekends"
              checked={showWeekends}
              onChange={(e) => setShowWeekends(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
            />
            <label htmlFor="showWeekends" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Include Weekends
            </label>
          </div>
          
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="showRollingAverage"
              checked={showRollingAverage}
              onChange={(e) => setShowRollingAverage(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
            />
            <label htmlFor="showRollingAverage" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Show 5-Day Rolling Average
            </label>
          </div>
        </div>

        {/* Button */}
        <div className="mt-6 text-center">
          <button
            onClick={loadData}
            disabled={loading}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {loading ? 'Loading...' : data ? 'Refresh Rate Insights' : 'Show Rate Insights'}
          </button>
        </div>
      </Section>

      {/* Loading State for Data Section */}
      {loading && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-soft border border-gray-200 dark:border-gray-700 p-8">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading rate insights data...</p>
          </div>
        </div>
      )}

      {/* Error State for Data Section */}
      {error && !loading && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-soft border border-gray-200 dark:border-gray-700 p-8">
          <div className="text-center">
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <button
              onClick={loadData}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Rate Chart */}
      {!loading && !error && data && getFilteredDailyData.length > 0 && (
        <Section section="chart" icon={TrendingUp} title="Load Volume & Rate Trends" iconColor="text-indigo-600">
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={getFilteredDailyData}
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-300 dark:stroke-gray-600" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 10, angle: -45, textAnchor: 'end' }}
                  className="text-gray-600 dark:text-gray-400"
                  interval={0}
                  height={80}
                  tickFormatter={(value) => {
                    // Parse date string manually to avoid timezone conversion
                    const [, month, day] = value.split('-');
                    return `${parseInt(month)}/${parseInt(day)}`;
                  }}
                />
                <YAxis 
                  yAxisId="left"
                  orientation="left"
                  tick={{ fontSize: 12 }}
                  className="text-gray-600 dark:text-gray-400"
                  tickFormatter={(value) => value.toLocaleString()}
                  label={{ value: 'Load Count', angle: -90, position: 'insideLeft' }}
                />
                <YAxis 
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 12 }}
                  className="text-gray-600 dark:text-gray-400"
                  domain={[0, 'dataMax * 1.10']}
                  ticks={(() => {
                    // Generate ticks at $0.50 intervals
                    const maxRate = Math.max(...(getFilteredDailyData.map(d => d.avg_rate_per_mile || 0)));
                    const maxTick = Math.ceil(maxRate * 1.10 * 2) / 2; // Round up to nearest 0.50
                    const ticks = [];
                    for (let i = 0; i <= maxTick; i += 0.5) {
                      ticks.push(i);
                    }
                    return ticks;
                  })()}
                  tickFormatter={(value) => `$${value.toFixed(2)}`}
                  label={{ value: 'Rate per Mile', angle: 90, position: 'insideRight' }}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'rgb(31 41 55)',
                    border: '1px solid rgb(75 85 99)',
                    borderRadius: '0.5rem',
                    color: 'white'
                  }}
                  formatter={(value, name) => {
                    if (name === 'Load Count') {
                      return [value.toLocaleString(), name];
                    }
                    return [`$${parseFloat(value).toFixed(2)}`, name];
                  }}
                  labelFormatter={(label) => {
                    // Parse date string manually to avoid timezone conversion
                    const [year, month, day] = label.split('-');
                    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                    return date.toLocaleDateString('en-US', { 
                      weekday: 'short', 
                      month: 'short', 
                      day: 'numeric' 
                    });
                  }}
                />
                <Legend />
                <Bar
                  yAxisId="left"
                  dataKey="total_loads"
                  fill="#3B82F6"
                  name="Load Count"
                  opacity={0.7}
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="avg_rate_per_mile" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  name="Rate/Mile"
                  dot={{ fill: '#10b981', r: 4 }}
                  activeDot={{ r: 6 }}
                />
                {showRollingAverage && (
                  <Line 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="rolling_5day_rpm" 
                    stroke="#f97316" 
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    name="5-Day Avg Rate/Mile"
                    dot={false}
                    connectNulls={false}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          
          {trendCards}
        </Section>
      )}

      {/* Daily Breakdown */}
      {!loading && !error && data && (
        <Section section="daily" icon={Calendar} title="Daily Breakdown" iconColor="text-orange-600">
          <Table
            headers={[
              { label: 'Date' },
              { label: 'Total Loads', align: 'text-right' },
              { label: 'Avg Rate/Mile', align: 'text-right' },
              { label: 'Avg Payment', align: 'text-right' },
              { label: '5-Day Avg Rate', align: 'text-right' }
            ]}
            data={getFilteredDailyData || []}
            renderRow={(daily) => (
              <>
                <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                  {(() => {
                    // Parse date string manually to avoid timezone conversion
                    const [year, month, day] = daily.date.split('-');
                    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                    return date.toLocaleDateString('en-US', { 
                      weekday: 'short', 
                      month: 'short', 
                      day: 'numeric' 
                    });
                  })()}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 dark:text-white text-right">{(daily.total_loads || 0).toLocaleString()}</td>
                <td className="px-6 py-4 text-sm text-gray-900 dark:text-white text-right">${(daily.avg_rate_per_mile || 0).toFixed(2)}</td>
                <td className="px-6 py-4 text-sm text-gray-900 dark:text-white text-right">${Math.round(daily.avg_payment || 0).toLocaleString()}</td>
                <td className="px-6 py-4 text-sm text-gray-900 dark:text-white text-right">
                  {daily.rolling_5day_rpm ? `$${daily.rolling_5day_rpm.toFixed(2)}` : '-'}
                </td>
              </>
            )}
          />
        </Section>
      )}

      {/* States */}
      {!loading && !error && data && (
        <Section section="states" icon={MapPin} title="States In vs States Out" iconColor="text-green-600">
          <div className="flex justify-end mb-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600 dark:text-gray-400">Show top:</label>
              <input
                type="number" min="1" max="50" value={displayOptions.topStates}
                onChange={(e) => setDisplayOptions(prev => ({ ...prev, topStates: parseInt(e.target.value) }))}
                className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white dark:bg-gray-800">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleStatesSort('state')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>State</span>
                      {statesSortConfig.key === 'state' && (
                        <span>{statesSortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleStatesSort('loads_out')}
                  >
                    <div className="flex items-center justify-end space-x-1">
                      <span>Loads Out</span>
                      {statesSortConfig.key === 'loads_out' && (
                        <span>{statesSortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleStatesSort('loads_in')}
                  >
                    <div className="flex items-center justify-end space-x-1">
                      <span>Loads In</span>
                      {statesSortConfig.key === 'loads_in' && (
                        <span>{statesSortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleStatesSort('total')}
                  >
                    <div className="flex items-center justify-end space-x-1">
                      <span>Total</span>
                      {statesSortConfig.key === 'total' && (
                        <span>{statesSortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleStatesSort('ratio_in_to_out')}
                  >
                    <div className="flex items-center justify-end space-x-1">
                      <span>In/Out Ratio</span>
                      {statesSortConfig.key === 'ratio_in_to_out' && (
                        <span>{statesSortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleStatesSort('total_avg_rpm')}
                  >
                    <div className="flex items-center justify-end space-x-1">
                      <span>Total Avg RPM</span>
                      {statesSortConfig.key === 'total_avg_rpm' && (
                        <span>{statesSortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleStatesSort('avg_rate_out')}
                  >
                    <div className="flex items-center justify-end space-x-1">
                      <span>Avg Rate Out</span>
                      {statesSortConfig.key === 'avg_rate_out' && (
                        <span>{statesSortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleStatesSort('avg_rate_in')}
                  >
                    <div className="flex items-center justify-end space-x-1">
                      <span>Avg Rate In</span>
                      {statesSortConfig.key === 'avg_rate_in' && (
                        <span>{statesSortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleStatesSort('avg_pmt_out')}
                  >
                    <div className="flex items-center justify-end space-x-1">
                      <span>Avg Pmt Out</span>
                      {statesSortConfig.key === 'avg_pmt_out' && (
                        <span>{statesSortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleStatesSort('avg_pmt_in')}
                  >
                    <div className="flex items-center justify-end space-x-1">
                      <span>Avg Pmt In</span>
                      {statesSortConfig.key === 'avg_pmt_in' && (
                        <span>{statesSortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {getSortedStatesData().map((state, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{state.state}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white text-right">{(state.loads_out || 0).toLocaleString()}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white text-right">{(state.loads_in || 0).toLocaleString()}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white text-right">{((state.loads_in || 0) + (state.loads_out || 0)).toLocaleString()}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white text-right">{(state.ratio_in_to_out || 0).toFixed(2)}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white text-right">${(state.total_avg_rpm || 0).toFixed(2)}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white text-right">${(state.avg_rate_out || 0).toFixed(2)}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white text-right">${(state.avg_rate_in || 0).toFixed(2)}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white text-right">${Math.round(state.avg_pmt_out || 0).toLocaleString()}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white text-right">${Math.round(state.avg_pmt_in || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Cities */}
      {!loading && !error && data && (
        <Section section="cities" icon={MapPin} title="Most Popular Cities" iconColor="text-blue-600">
          <div className="flex justify-end mb-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600 dark:text-gray-400">Show top:</label>
              <input
                type="number" min="1" max="100" value={displayOptions.topCities}
                onChange={(e) => setDisplayOptions(prev => ({ ...prev, topCities: parseInt(e.target.value) }))}
                className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white dark:bg-gray-800">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleCitiesSort('city_state')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>City, State</span>
                      {citiesSortConfig.key === 'city_state' && (
                        <span>{citiesSortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleCitiesSort('loads_out')}
                  >
                    <div className="flex items-center justify-end space-x-1">
                      <span>Loads Out</span>
                      {citiesSortConfig.key === 'loads_out' && (
                        <span>{citiesSortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleCitiesSort('loads_in')}
                  >
                    <div className="flex items-center justify-end space-x-1">
                      <span>Loads In</span>
                      {citiesSortConfig.key === 'loads_in' && (
                        <span>{citiesSortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleCitiesSort('ratio_in_to_out')}
                  >
                    <div className="flex items-center justify-end space-x-1">
                      <span>In/Out Ratio</span>
                      {citiesSortConfig.key === 'ratio_in_to_out' && (
                        <span>{citiesSortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleCitiesSort('total_avg_rpm')}
                  >
                    <div className="flex items-center justify-end space-x-1">
                      <span>Total Avg RPM</span>
                      {citiesSortConfig.key === 'total_avg_rpm' && (
                        <span>{citiesSortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleCitiesSort('avg_pmt_out')}
                  >
                    <div className="flex items-center justify-end space-x-1">
                      <span>Avg Pmt Out</span>
                      {citiesSortConfig.key === 'avg_pmt_out' && (
                        <span>{citiesSortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleCitiesSort('avg_pmt_in')}
                  >
                    <div className="flex items-center justify-end space-x-1">
                      <span>Avg Pmt In</span>
                      {citiesSortConfig.key === 'avg_pmt_in' && (
                        <span>{citiesSortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {getSortedCitiesData().map((city, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 text-sm font-medium">
                      <button
                        onClick={() => handleCityClick(city.city_state)}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline cursor-pointer transition-colors"
                        title={`Click to view details for ${city.city_state}`}
                      >
                        {city.city_state}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white text-right">{(city.loads_out || 0).toLocaleString()}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white text-right">{(city.loads_in || 0).toLocaleString()}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white text-right">{(city.ratio_in_to_out || 0).toFixed(2)}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white text-right">${(city.total_avg_rpm || 0).toFixed(2)}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white text-right">${Math.round(city.avg_pmt_out || 0).toLocaleString()}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white text-right">${Math.round(city.avg_pmt_in || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Equipment */}
      {!loading && !error && data && (
        <Section section="equipment" icon={Truck} title="Equipment Types" iconColor="text-purple-600">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Table
              headers={[{ label: 'Equipment' }, { label: 'Count', align: 'text-right' }]}
              data={data.equipment_data?.slice(0, 10) || []}
              renderRow={(equipment) => (
                <>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{equipment.equipment}</td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white text-right">{(equipment.count || 0).toLocaleString()}</td>
                </>
              )}
            />
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPie>
                  <Pie
                    data={data.equipment_data?.slice(0, 6) || []}
                    cx="50%" cy="50%" labelLine={false}
                    label={({ equipment, percent }) => `${equipment} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80} fill="#8884d8" dataKey="count"
                  >
                    {(data.equipment_data?.slice(0, 6) || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </RechartsPie>
              </ResponsiveContainer>
            </div>
          </div>
        </Section>
      )}

      {/* Total */}
      {!loading && !error && data && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Total Loads Analyzed</h3>
            <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{(data.total_records || 0).toLocaleString()}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {filters.dateRange.start && filters.dateRange.end ? 
                `From ${filters.dateRange.start} to ${filters.dateRange.end}` : 
                'Select date range and click Refresh'}
            </p>
          </div>
        </div>
      )}

      {/* City Details Modal */}
      {cityDetailsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {cityDetailsModal.city}, {cityDetailsModal.state} - Load Details
              </h2>
              <div className="flex items-center space-x-4">
                {cityDetailsModal.data && (
                  <button
                    onClick={toggleRawDataView}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    {cityDetailsModal.showRawData ? 'Show Summary' : 'View Raw Data'}
                  </button>
                )}
                <button
                  onClick={closeCityDetailsModal}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {cityDetailsModal.loading && (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                  <p className="text-gray-600 dark:text-gray-400">Loading city details...</p>
                </div>
              )}

              {cityDetailsModal.error && (
                <div className="text-center py-8">
                  <p className="text-red-600 dark:text-red-400 mb-4">{cityDetailsModal.error}</p>
                  <button
                    onClick={() => handleCityClick(`${cityDetailsModal.city}, ${cityDetailsModal.state}`)}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  >
                    Retry
                  </button>
                </div>
              )}

              {cityDetailsModal.data && (
                <div className="space-y-6">
                  {/* View Toggle Information */}
                  <div className="text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {cityDetailsModal.showRawData
                        ? `Showing detailed load records (${cityDetailsModal.data.loads?.length || 0} loads)`
                        : 'Showing summary statistics'
                      }
                    </p>
                  </div>

                  {!cityDetailsModal.showRawData ? (
                    // Summary View
                    <div className="space-y-6">
                      {/* Summary Statistics */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                          <h3 className="text-sm font-medium text-blue-600 dark:text-blue-400 mb-1">Total Loads</h3>
                          <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                            {(cityDetailsModal.data.total_loads || 0).toLocaleString()}
                          </p>
                        </div>
                        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                          <h3 className="text-sm font-medium text-green-600 dark:text-green-400 mb-1">Loads In</h3>
                          <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                            {(cityDetailsModal.data.loads_in || 0).toLocaleString()}
                          </p>
                        </div>
                        <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                          <h3 className="text-sm font-medium text-orange-600 dark:text-orange-400 mb-1">Loads Out</h3>
                          <p className="text-2xl font-bold text-orange-900 dark:text-orange-100">
                            {(cityDetailsModal.data.loads_out || 0).toLocaleString()}
                          </p>
                        </div>
                        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                          <h3 className="text-sm font-medium text-purple-600 dark:text-purple-400 mb-1">Avg RPM</h3>
                          <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                            ${(cityDetailsModal.data.total_avg_rpm || 0).toFixed(2)}
                          </p>
                        </div>
                      </div>

                      {/* Top Routes Analysis */}
                      {(cityDetailsModal.data.top_destinations || cityDetailsModal.data.top_origins) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Top Destinations */}
                          {cityDetailsModal.data.top_destinations && cityDetailsModal.data.top_destinations.length > 0 && (
                            <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg">
                              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Top Destinations</h3>
                              <div className="space-y-2">
                                {cityDetailsModal.data.top_destinations.slice(0, 5).map((dest, index) => (
                                  <div key={index} className="border-b border-gray-200 dark:border-gray-600 last:border-b-0 pb-2 last:pb-0 mb-2 last:mb-0">
                                    <div className="flex justify-between items-start">
                                      <span className="font-medium text-gray-900 dark:text-white">{dest.destination}</span>
                                      <span className="text-sm text-gray-600 dark:text-gray-400">{dest.load_count} loads</span>
                                    </div>
                                    <div className="mt-1 text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
                                      <div>${Math.round(dest.avg_payment).toLocaleString()} avg payment</div>
                                      <div>{Math.round(dest.avg_mileage || 0).toLocaleString()} miles avg</div>
                                      <div>${(dest.avg_rate || 0).toFixed(2)} RPM</div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Top Origins */}
                          {cityDetailsModal.data.top_origins && cityDetailsModal.data.top_origins.length > 0 && (
                            <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg">
                              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Top Origins</h3>
                              <div className="space-y-2">
                                {cityDetailsModal.data.top_origins.slice(0, 5).map((origin, index) => (
                                  <div key={index} className="border-b border-gray-200 dark:border-gray-600 last:border-b-0 pb-2 last:pb-0 mb-2 last:mb-0">
                                    <div className="flex justify-between items-start">
                                      <span className="font-medium text-gray-900 dark:text-white">{origin.origin}</span>
                                      <span className="text-sm text-gray-600 dark:text-gray-400">{origin.load_count} loads</span>
                                    </div>
                                    <div className="mt-1 text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
                                      <div>${Math.round(origin.avg_payment).toLocaleString()} avg payment</div>
                                      <div>{Math.round(origin.avg_mileage || 0).toLocaleString()} miles avg</div>
                                      <div>${(origin.avg_rate || 0).toFixed(2)} RPM</div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    // Raw Data View
                    <div>
                      {/* Raw Data Table */}
                      {cityDetailsModal.data.loads && cityDetailsModal.data.loads.length > 0 ? (
                        <div className="overflow-x-auto max-h-[60vh] border border-gray-200 dark:border-gray-700 rounded-lg">
                          <table className="min-w-full bg-white dark:bg-gray-800">
                            <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Origin</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Destination</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Equipment</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Payment</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Miles</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">RPM</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Pickup Date</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                              {cityDetailsModal.data.loads.map((load, index) => (
                                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                  <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                                    {load.origin_city}, {load.origin_state}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                                    {load.destination_city}, {load.destination_state}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{load.equipment}</td>
                                  <td className="px-4 py-2 text-sm text-gray-900 dark:text-white text-right">
                                    ${(load.payment || 0).toLocaleString()}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-900 dark:text-white text-right">
                                    {(load.miles || 0).toLocaleString()}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-900 dark:text-white text-right">
                                    ${(load.rate_per_mile || 0).toFixed(2)}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                                    {load.pickup_date || 'N/A'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <p className="text-gray-600 dark:text-gray-400">No load details available for the selected filters.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RateInsights;